/**
 * Cross-app events for the Orchestration Console drawer.
 * Host apps dispatch these; ConsoleDrawer / ConsoleBottomPanel subscribe.
 */

export const CONSOLE_TOGGLE_EVENT = "flowstate:console:toggle";
export const CONSOLE_OPEN_EVENT = "flowstate:console:open";
export const CONSOLE_FOCUS_EVENT = "flowstate:console:focus";

/** Focus / filter payload when opening the console from Cortex Mesh (or elsewhere). */
export type ConsoleFocusDetail = {
  /** Free-text terms matched against event_type, summary, and payload fields. */
  terms?: string[];
  /** Prefer this domain tab when present. */
  domain?: string;
  /** Human label for the filter chip (e.g. agent name). */
  label?: string;
  /** Optional agent / mesh id for debugging. */
  agentId?: string;
  /** Optional cortex run id — exact match when set. */
  runId?: string;
};

export function toggleOrchestrationConsole(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(CONSOLE_TOGGLE_EVENT));
}

export function openOrchestrationConsole(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(CONSOLE_OPEN_EVENT));
}

/**
 * Open (or keep open) the console and apply a focus filter.
 * Dispatches both focus and open so callers need one call.
 */
export function focusOrchestrationConsole(detail: ConsoleFocusDetail): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(CONSOLE_FOCUS_EVENT, { detail }),
  );
  openOrchestrationConsole();
}

export function subscribeOrchestrationConsole(
  handlers: {
    onToggle?: () => void;
    onOpen?: () => void;
    onFocus?: (detail: ConsoleFocusDetail) => void;
  },
): () => void {
  if (typeof window === "undefined") return () => {};

  const onToggle = () => handlers.onToggle?.();
  const onOpen = () => handlers.onOpen?.();
  const onFocus = (e: Event) => {
    const detail = (e as CustomEvent<ConsoleFocusDetail>).detail;
    if (detail) handlers.onFocus?.(detail);
  };

  window.addEventListener(CONSOLE_TOGGLE_EVENT, onToggle);
  window.addEventListener(CONSOLE_OPEN_EVENT, onOpen);
  window.addEventListener(CONSOLE_FOCUS_EVENT, onFocus);

  return () => {
    window.removeEventListener(CONSOLE_TOGGLE_EVENT, onToggle);
    window.removeEventListener(CONSOLE_OPEN_EVENT, onOpen);
    window.removeEventListener(CONSOLE_FOCUS_EVENT, onFocus);
  };
}
